/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import * as React from 'react';

import { ILogoLinkData } from '../cus-header.props.autogenerated';
import { useGenderCookieValue } from '../../../shared/services/gender-cookie-service/hooks/use-gender-cookie-value';
import { buildGenderUrl, getGenderUrlsForLocale } from '../../../shared/services/gender-cookie-service/gender-cookie-service';
import { ILocaleGenderUrls } from '../../../shared/services/gender-cookie-service/gender-cookie-service.types';

export interface ICusHeaderLogoProps {
    image: Msdyn365.IImageData;
    link?: ILogoLinkData;
    className?: string;
    requestContext: Msdyn365.IRequestContext;
    gridSettings: Msdyn365.IGridSettings;
}

const logoViewport = { q: 'w=320&h=96&m=6', w: 0, h: 0 };

const imageSettings: Msdyn365.IImageSettings = {
    viewports: {
        xs: logoViewport,
        sm: logoViewport,
        md: logoViewport,
        lg: logoViewport,
        xl: logoViewport
    },
    disableLazyLoad: true
};

/**
 * Renders the header logo image and its home link.
 */
export const CusHeaderLogo = (props: ICusHeaderLogoProps): JSX.Element => {
    const gender = useGenderCookieValue(props.requestContext);
    const localeGenderUrls: ILocaleGenderUrls[] | undefined = props.requestContext.app.config.localeGenderUrls;
    const genderUrlsForLocale = getGenderUrlsForLocale(localeGenderUrls, props.requestContext.locale);
    const genderPath = gender && genderUrlsForLocale?.[gender];
    const destinationUrl = genderPath
        ? buildGenderUrl(props.requestContext.app.routes.home.destinationUrl, genderPath)
        : props.link?.linkUrl.destinationUrl;

    let logoImageUrl = props.image.src;
    if (logoImageUrl) {
        try {
            const url = new URL(logoImageUrl);
            url.search += `${url.search ? '&' : ''}${logoViewport.q}`;
            logoImageUrl = url.href;
        } catch {
            // Relative URLs are also left unchanged by the SDK image component.
        }
    }

    const image = (
        <Msdyn365.Image
            {...props.image}
            requestContext={props.requestContext}
            gridSettings={props.gridSettings}
            imageSettings={imageSettings}
            loading='eager'
            fetchPriority={Msdyn365.ImageFetchPriority.High}
            loadFailureBehavior='default'
        />
    );

    return (
        <div className={props.className}>
            {logoImageUrl && (
                <Msdyn365.HtmlHeadInclude>
                    <link rel='preload' as='image' href={logoImageUrl} />
                </Msdyn365.HtmlHeadInclude>
            )}
            {destinationUrl ? (
                <a
                    href={destinationUrl}
                    aria-label={props.link?.ariaLabel}
                    target={props.link?.openInNewTab ? '_blank' : undefined}
                    rel='noopener noreferrer'
                >
                    {image}
                </a>
            ) : (
                image
            )}
        </div>
    );
};
